import { SortDirections } from '@/models/filters.model';
import { OrdersStatus } from '@/models/orders.model';

export enum DashboardEndpoints {
  DASHBOARD = 'dashboard',
  DASHBOARD_TRENDS = 'dashboard/trends',
  DASHBOARD_CATEGORY_STATS = 'dashboard/category-stats',
  DASHBOARD_SETTLEMENT_STATS = 'dashboard/settlement-stats',
  DASHBOARD_BUYERS_STATS = 'dashboard/user-stats',
  DASHBOARD_MAIN_METRICS = 'dashboard/main-metrics',
  DASHBOARD_MAIN_METRICS_TOP_SUPPLIERS = 'dashboard/main-metrics/top-suppliers',
  DASHBOARD_MAIN_METRICS_TOP_PRODUCTS = 'dashboard/main-metrics/top-products',
}

export type MainMetricsGroupBy = 'day' | 'week';
export type MainMetricsChartMode = 'amount' | 'count';
export type TopSortKey = 'amount' | 'orders' | 'avg_check';
export type TopOrderType = 'active' | 'completed';

export interface IAdminMainMetricsQuery {
  date_from?: string;
  date_to?: string;
  group_by?: MainMetricsGroupBy;
  top_suppliers_order_type?: TopOrderType;
  top_products_order_type?: TopOrderType;
}

export interface IAdminTopListQuery extends IAdminMainMetricsQuery {
  sort?: TopSortKey;
  page?: number;
  per_page?: number;
}

export interface IPeriodRange {
  from: string | null;
  to: string | null;
}

export interface IMetricChange {
  value: number;
  change_percent: number | null;
}

export interface IShareMetricChange {
  value: number;
  change_pp: number | null;
}

export interface IRecordMetric {
  value: number;
  date: string;
}

export interface IAdminRecords {
  max_active_orders_per_day: IRecordMetric;
  max_active_orders_amount_per_day: IRecordMetric;
}

export interface IAdminCancelledKpi {
  count: number;
  share_percent: number;
  amount: number;
}

export interface IAdminMainMetricsKpis {
  active_orders_count: IMetricChange;
  active_orders_amount: IMetricChange;
  avg_check: IMetricChange;
  cancelled: IAdminCancelledKpi;
  new_users: IMetricChange;
  purchase_rate_pr: IShareMetricChange;
  customer_ltv: IMetricChange;
}

export interface IDualSeriesItem {
  date: string;
  amount: number;
  count?: number;
  prev_amount?: number;
  prev_count?: number;
}

export interface IStockSeriesItem {
  date: string;
  amount: number;
  prev_amount?: number;
}

export interface INamedShareItem {
  id: string;
  name: string;
  amount: number;
  share_percent: number;
}

export interface IPaymentTypeItem {
  code: string;
  label: string;
  amount: number;
  share_percent: number;
}

export interface IPaidBlock {
  amount: IMetricChange;
  count: IMetricChange;
  series: IDualSeriesItem[];
  payment_types: IPaymentTypeItem[];
}

export interface IStockSupplierRow {
  rank: number;
  id: string;
  name: string;
  amount: number;
  share_percent: number;
  change_percent: number | null;
}

export interface IStockBlock {
  as_of: string;
  total_amount: number;
  growth_amount: number;
  series: IStockSeriesItem[];
  by_region: INamedShareItem[];
  top_suppliers: IStockSupplierRow[];
}

export interface ICatalogSeriesItem {
  date: string;
  total: number;
  published: number;
  hidden: number;
  deleted: number;
}

export interface ICatalogBlock {
  sku_total: number;
  published: number;
  hidden: number;
  deleted: number;
  series: ICatalogSeriesItem[];
}

export interface ISuppliersBlock {
  total: IMetricChange;
  growth: number;
  departed: number;
  series: Array<{ date: string; total: number; prev_total?: number }>;
}

export interface IEngagementBlock {
  percent: number;
  change_pp: number | null;
  avg_active_shops_per_day: number;
  daily: Array<{ date: string; percent: number }>;
}

export interface ITopSupplierRow {
  rank: number;
  id: string;
  name: string;
  amount: number;
  orders_count: number;
  avg_check: number;
  change_percent: number | null;
}

export interface ITopProductRow {
  rank: number;
  id: string;
  name: string;
  sku: string;
  supplier_name: string;
  amount: number;
  orders_count: number;
  avg_check: number;
  change_percent: number | null;
}

export interface IAdminMainMetricsData {
  period: IPeriodRange;
  previous_period: IPeriodRange | null;
  records: IAdminRecords;
  kpis: IAdminMainMetricsKpis;
  active_orders_series: IDualSeriesItem[];
  paid: IPaidBlock;
  stock: IStockBlock;
  catalog: ICatalogBlock;
  suppliers: ISuppliersBlock;
  engagement: IEngagementBlock;
  top_suppliers: ITopSupplierRow[];
  top_products: ITopProductRow[];
}

export interface IGetAdminMainMetricsResponse {
  data: IAdminMainMetricsData;
}

export interface ITrend {
  name: string;
  value: number;
  type: SortDirections;
}

export interface IBuyersData {
  new: string;
  percentage: string;
}

export interface IOrderStatusesItem {
  status: OrdersStatus;
  value: number;
}

export interface ITopProductsItem {
  id: string;
  name: string;
  sku: string;
  slug: string;
  photo: string;
}

export interface ICityStatisticDailyStatsItem {
  date: string;
  count: string;
  price: string;
  avg_price: string;
}

export interface ICategoryStatisticItem {
  date: string;
  revenue: string;
}

export interface IDashboardData {
  orders_today: number;
  orders_total: number;
  revenue_today: number;
  revenue_total: number;
  products_active: number;
  products_hidden: number;
  trends: ITrend[];
  categories_count: number;
  statuses_stats: IOrderStatusesItem[];
  top_products: ITopProductsItem[];
}

export interface ICityStatisticData {
  total_count: number;
  total_price: number;
  total_avg_price: number;
  daily_stats: ICityStatisticDailyStatsItem[];
}

// ----------------- DTOs ----------------

export interface IGetCityStatisticDTO {
  settlement_id?: string;
  date_from?: string;
  date_to?: string;
}

export interface IGetCategoryStatisticDTO {
  category_id?: string;
  date_from?: string;
  date_to?: string;
}

// ------------ Responses --------------

export interface IGetDashboardDataResponse {
  data: IDashboardData;
}

export interface IGetCityStatisticResponse {
  data: ICityStatisticData;
}

export interface IGetCategoryStatisticResponse {
  data: ICategoryStatisticItem[];
}

export interface IGetTrendsResponse {
  data: ITrend[];
}

export interface IGetBuyersDataResponse {
  data: IBuyersData;
}
